import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { InfoIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, ArrowUpDownIcon, ArrowRightIcon, ChevronRightIcon, InfoIcon as InfoCircleIcon, AlertTriangleIcon, XIcon, ShieldIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
export function BalanceSummary() {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedDates, setExpandedDates] = useState<Record<string, string>>({
    incoming: '5 Dec 2025',
    available: '5 Dec 2025',
    reserved: '15 Dec 2025'
  });
  const [simulateNegative, setSimulateNegative] = useState(false);
  const [showMinBanner, setShowMinBanner] = useState(true);
  const [showMinBalanceModal, setShowMinBalanceModal] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [minBalanceSaved, setMinBalanceSaved] = useState(false);
  const displayBalance = simulateNegative ? -4232.75 : 61767.25;
  const isNegative = displayBalance < 0;
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const toggleDate = (section: string, date: string) => {
    setExpandedDates(prev => ({
      ...prev,
      [section]: prev[section] === date ? '' : date
    }));
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  // Data for Incoming section
  const incomingChartData = [{
    date: '5 Dec',
    amount: 1000
  }, {
    date: '6 Dec',
    amount: 800
  }, {
    date: '8 Dec',
    amount: 620
  }, {
    date: '9 Dec',
    amount: 380
  }, {
    date: '10 Dec',
    amount: 200
  }];
  const incomingTransactions = [{
    id: 'ch_4Ax1a1',
    customer: 'Malee Fashion',
    amount: 720
  }, {
    id: 'ch_4Ax1a2',
    customer: 'Chai Electronics',
    amount: 480
  }];
  // Data for Available section
  const availableChartData = [{
    date: '5 Dec',
    amount: 45230
  }, {
    date: '6 Dec',
    amount: 3500
  }, {
    date: '9 Dec',
    amount: 2100
  }, {
    date: '10 Dec',
    amount: 800
  }, {
    date: '11 Dec',
    amount: 600
  }, {
    date: '12 Dec',
    amount: 200
  }];
  const availableTransactions = [{
    id: 'ch_3Qx1a2',
    customer: 'Somchai Wattanachai',
    amount: 18500
  }, {
    id: 'ch_3Qx1b7',
    customer: 'Siriporn Trading',
    amount: 12450.8
  }, {
    id: 'ch_3Qx1c4',
    customer: 'Nattapong Wongsakul',
    amount: 8750
  }, {
    id: 'ch_3Qx1d9',
    customer: 'Somchai Enterprises Co., Ltd.',
    amount: 5530
  }];
  // Data for Reserved section
  const reservedChartData = [{
    date: '15 Dec',
    amount: 1850
  }, {
    date: '22 Dec',
    amount: 2100
  }, {
    date: '29 Dec',
    amount: 1750
  }, {
    date: '5 Jan',
    amount: 2400
  }, {
    date: '12 Jan',
    amount: 1980
  }, {
    date: '19 Jan',
    amount: 2370
  }];
  const reservedTransactions = [{
    id: 'rsv_1a',
    customer: 'Somchai Wattanachai',
    amount: 925
  }, {
    id: 'rsv_1b',
    customer: 'Siriporn Trading',
    amount: 625
  }, {
    id: 'rsv_1c',
    customer: 'Nattapong Wongsakul',
    amount: 300
  }];
  // Data for On hold section
  const onHoldChartData = [{
    date: '10 Dec',
    amount: 1500
  }, {
    date: '15 Dec',
    amount: 2000
  }, {
    date: '20 Dec',
    amount: 2000
  }];
  const onHoldTransactions = [{
    id: 'hld_1a',
    customer: 'Review: High risk txn',
    amount: 2500
  }, {
    id: 'hld_1b',
    customer: 'Review: Unusual volume',
    amount: 3000
  }];
  const balanceTypes = [{
    key: 'incoming',
    label: 'Incoming',
    description: 'Recent payments still being processed before they become available',
    amount: 3250.0,
    colorDot: 'bg-amber-400',
    colorPrimary: '#F59E0B',
    colorSecondary: '#FCD34D',
    colorBadgeBg: 'bg-amber-50',
    colorBadgeBorder: 'border-amber-200',
    colorBadgeText: 'text-amber-600',
    sectionTitle: 'BECOMING AVAILABLE',
    badgeText: 'Next',
    chartData: incomingChartData,
    transactions: incomingTransactions,
    collapsedDates: [{
      date: '6 Dec 2025',
      count: 1,
      amount: 850
    }, {
      date: '8 Dec 2025',
      count: 1,
      amount: 620
    }, {
      date: '9 Dec 2025',
      count: 1,
      amount: 380
    }, {
      date: '10 Dec 2025',
      count: 1,
      amount: 200
    }]
  }, {
    key: 'available',
    label: 'Available',
    description: 'Funds you can use now for payouts, refunds, and account charges',
    amount: 61767.25,
    negativeAmount: -4232.75,
    colorDot: 'bg-purple-500',
    colorPrimary: '#8B5CF6',
    colorSecondary: '#C4B5FD',
    colorBadgeBg: 'bg-purple-50',
    colorBadgeBorder: 'border-purple-200',
    colorBadgeText: 'text-purple-700',
    sectionTitle: 'UPCOMING PAYOUT SCHEDULE',
    badgeText: 'Next payout',
    chartData: availableChartData,
    transactions: availableTransactions,
    collapsedDates: [{
      date: '6 Dec 2025',
      count: 3,
      amount: 10000
    }, {
      date: '9 Dec 2025',
      count: 2,
      amount: 3250.45
    }, {
      date: '10 Dec 2025',
      count: 1,
      amount: 1820
    }, {
      date: '11 Dec 2025',
      count: 1,
      amount: 966
    }]
  }, {
    key: 'reserved',
    label: 'Reserved',
    description: '10% of payments held for 90 days per your reserve policy',
    amount: 12450.0,
    colorDot: 'bg-orange-400',
    colorPrimary: '#F97316',
    colorSecondary: '#FDBA74',
    colorBadgeBg: 'bg-orange-50',
    colorBadgeBorder: 'border-orange-200',
    colorBadgeText: 'text-orange-600',
    sectionTitle: 'UPCOMING RESERVE RELEASES',
    badgeText: 'Next release',
    infoBanner: <div className="flex items-start gap-3 p-4 mb-8 bg-orange-50 border border-orange-100 rounded-lg" data-id="element-4858">
          <InfoCircleIcon size={18} className="text-orange-500 flex-shrink-0 mt-0.5" data-id="element-4859" />
          <p className="text-sm text-gray-700" data-id="element-4860">
            <span className="font-semibold text-gray-900" data-id="element-4861">Reserve policy:</span>{' '}
            10% of each payment is held for 90 days as a safeguard against
            chargebacks and disputes. Funds are automatically released to your
            available balance after the holding period.
          </p>
        </div>,
    chartData: reservedChartData,
    transactions: reservedTransactions,
    collapsedDates: [{
      date: '22 Dec 2025',
      count: 2,
      amount: 2100
    }, {
      date: '29 Dec 2025',
      count: 1,
      amount: 1750
    }, {
      date: '5 Jan 2026',
      count: 2,
      amount: 2400
    }, {
      date: '12 Jan 2026',
      count: 1,
      amount: 1980
    }, {
      date: '19 Jan 2026',
      count: 2,
      amount: 2370
    }]
  }, {
    key: 'onHold',
    label: 'On hold',
    description: 'Funds temporarily unavailable while certain transactions or account activity are reviewed',
    amount: 5500.0,
    colorDot: 'bg-red-400',
    colorPrimary: '#EF4444',
    colorSecondary: '#FCA5A5',
    colorBadgeBg: 'bg-red-50',
    colorBadgeBorder: 'border-red-200',
    colorBadgeText: 'text-red-600',
    sectionTitle: 'EXPECTED RESOLUTION',
    badgeText: 'Next review',
    chartData: onHoldChartData,
    transactions: onHoldTransactions,
    collapsedDates: [{
      date: '15 Dec 2025',
      count: 1,
      amount: 2000
    }, {
      date: '20 Dec 2025',
      count: 1,
      amount: 2000
    }]
  }];
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg" data-id="element-4862">
          <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-4863">{label}</p>
          <p className="text-sm text-gray-700" data-id="element-4864">
            Available : THB {formatCurrency(payload[0].value)}
          </p>
        </div>;
    }
    return null;
  };
  return <div className="max-w-7xl mx-auto py-8 px-4" data-id="element-4865">
      {/* Simulate toggle — dev tool */}
      <div className="mb-4 flex items-center gap-3" data-id="element-4866">
        <button onClick={() => {
        setSimulateNegative(!simulateNegative);
        setShowMinBanner(true);
        setMinBalanceSaved(false);
      }} className="relative flex-shrink-0" data-id="element-4867">
          <div className={`w-9 h-5 rounded-full transition-colors ${simulateNegative ? 'bg-red-500' : 'bg-gray-300'}`} data-id="element-4868">
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${simulateNegative ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-4869" />
          </div>
        </button>
        <span className="text-xs text-gray-500 font-medium" data-id="element-4870">
          Simulate negative balance
        </span>
      </div>

      {/* Negative balance warning banner */}
      {isNegative && showMinBanner && !minBalanceSaved && <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 relative" data-id="element-4871">
          <button onClick={() => setShowMinBanner(false)} className="absolute top-3 right-3 text-amber-700/60 hover:text-amber-800 transition-colors" data-id="element-4872">
            <XIcon size={16} data-id="element-4873" />
          </button>
          <div className="flex items-start gap-3" data-id="element-4874">
            <AlertTriangleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4875" />
            <div data-id="element-4876">
              <p className="text-sm font-semibold text-amber-900 mb-1" data-id="element-4877">
                Set a minimum to avoid negative balances
              </p>
              <p className="text-sm text-amber-800 leading-relaxed mb-3" data-id="element-4878">
                Your account has a negative balance due to refunds, disputes,
                reserves, or fees. We'll debit your account to cover the
                difference. Set a minimum to prevent negative balances.
              </p>
              <button onClick={() => navigate('/settings/payouts')} className="px-4 py-2 text-sm font-medium text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors shadow-sm" data-id="element-4879">
                Set minimum balance
              </button>
            </div>
          </div>
        </div>}

      {/* Minimum balance saved confirmation */}
      {isNegative && minBalanceSaved && <div className="mb-6 bg-green-50 border border-green-200 rounded-xl px-5 py-4" data-id="element-4880">
          <div className="flex items-center gap-3" data-id="element-4881">
            <ShieldIcon size={18} className="text-green-600 flex-shrink-0" data-id="element-4882" />
            <div data-id="element-4883">
              <p className="text-sm font-medium text-green-800" data-id="element-4884">
                Minimum balance set to THB{' '}
                {Number(minBalanceAmount).toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
              </p>
              <p className="text-xs text-green-600 mt-0.5" data-id="element-4885">
                We'll automatically top up your account when it drops below this
                threshold.
              </p>
            </div>
          </div>
        </div>}

      {/* Header */}
      <div className="mb-10" data-id="element-4886">
        <div className="flex items-center justify-between mb-2" data-id="element-4887">
          <div className="flex items-center gap-2" data-id="element-4888">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-4889">
              Available balance
            </h1>
            <div className="relative group cursor-pointer" data-id="element-4890">
              <InfoIcon size={20} className="text-gray-400 hover:text-gray-600" data-id="element-4891" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-4892">
                The sum of your available payments balances converted to your
                default currency.
              </div>
            </div>
          </div>
          <button onClick={() => {
          const now = new Date();
          const reportDate = now.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });
          const timestamp = now.toISOString();
          const rows = [['Report date', 'Merchant account ID', 'Merchant account name', 'Currency', 'Balance type', 'Balance amount', 'Balance status', 'Description', 'As of timestamp', 'Report generated at'], [reportDate, 'MERCH-001', 'ABC Store', 'THB', 'Incoming', '3250.00', 'Processing', 'Recent payments still being processed before they become available', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', 'THB', 'Available', simulateNegative ? '-4232.75' : '61767.25', 'Active', 'Funds you can use now for payouts, refunds, and account charges', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', 'THB', 'Reserved', '12450.00', 'Held', '10% of payments held for 90 days per your reserve policy', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', 'THB', 'On hold', '5500.00', 'Under review', 'Funds temporarily unavailable while certain transactions or account activity are reviewed', timestamp, timestamp], [reportDate, 'MERCH-001', 'ABC Store', 'THB', 'Total balance', simulateNegative ? '16967.25' : '82967.25', 'Active', 'Total of all balance types', timestamp, timestamp]];
          const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
          const blob = new Blob([csv], {
            type: 'text/csv;charset=utf-8;'
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `balance-summary-${now.toISOString().split('T')[0]}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4893">
            <DownloadIcon size={16} data-id="element-4894" />
            Export
          </button>
        </div>
        <div className={`text-4xl font-bold mb-6 flex items-center gap-2 ${isNegative ? 'text-red-600' : 'text-gray-900'}`} data-id="element-4895">
          {isNegative ? '−' : ''} THB {formatCurrency(Math.abs(displayBalance))}
          {isNegative && <AlertTriangleIcon size={24} className="text-red-500" data-id="element-4896" />}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8" data-id="element-4897">
        {/* Main Content - Balance Summary Table */}
        <div className="flex-1" data-id="element-4898">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4899">
            Balance summary
          </h2>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4900">
            {/* Table Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50/50" data-id="element-4901">
              <span className="text-sm font-medium text-gray-600" data-id="element-4902">
                Balance type
              </span>
              <span className="text-sm font-medium text-gray-600" data-id="element-4903">Amount</span>
            </div>

            <div className="divide-y divide-gray-200" data-id="element-4904">
              {balanceTypes.map(type => {
              const isAvailable = type.key === 'available';
              const displayAmt = isAvailable && simulateNegative && type.negativeAmount !== undefined ? type.negativeAmount : type.amount;
              const isNegativeAmt = displayAmt < 0;
              return <div key={type.key} data-id="element-4905">
                    <button onClick={() => toggleSection(type.key)} className="w-full flex items-start justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left" data-id="element-4906">
                      <div className="flex items-start gap-3" data-id="element-4907">
                        <div className={`w-3.5 h-3.5 ${type.colorDot} rounded-sm mt-1 flex-shrink-0`} data-id="element-4908"></div>
                        <div data-id="element-4909">
                          <div className="flex items-center gap-2" data-id="element-4910">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-4911">
                              {type.label}
                            </span>
                            {expandedSection === type.key ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-4912" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4913" />}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5" data-id="element-4914">
                            {type.description}
                          </p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium mt-0.5 whitespace-nowrap flex-shrink-0 ${isNegativeAmt ? 'text-red-600' : 'text-gray-900'}`} data-id="element-4915">
                        {isNegativeAmt ? '− ' : ''}THB{' '}
                        {formatCurrency(Math.abs(displayAmt))}
                      </span>
                    </button>

                    {expandedSection === type.key && <div className="px-6 py-6 bg-gray-50/50 border-t border-gray-100" data-id="element-4916">
                        {isAvailable && simulateNegative ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-4917">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-4918">
                              <AlertTriangleIcon size={20} className="text-gray-400" data-id="element-4919" />
                            </div>
                            <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-4920">
                              No upcoming payouts
                            </p>
                            <p className="text-xs text-gray-500 max-w-xs" data-id="element-4921">
                              Your account has a negative balance. Payouts are
                              paused until the balance is restored. Add funds or
                              set a minimum balance to resume payouts.
                            </p>
                          </div> : <>
                            {type.infoBanner}
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6" data-id="element-4922">
                              {type.sectionTitle}
                            </h4>

                            {/* Chart */}
                            <div className="h-48 mb-8" data-id="element-4923">
                              <ResponsiveContainer width="100%" height="100%" data-id="element-4924">
                                <BarChart data={type.chartData} margin={{
                          top: 0,
                          right: 0,
                          left: -20,
                          bottom: 0
                        }} data-id="element-4925">
                                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                            fontSize: 12,
                            fill: '#6B7280'
                          }} dy={10} data-id="element-4926" />
                                  <YAxis axisLine={false} tickLine={false} tick={{
                            fontSize: 12,
                            fill: '#6B7280'
                          }} tickFormatter={val => val >= 1000 ? `${val / 1000}K` : val} data-id="element-4927" />
                                  <Tooltip content={<CustomTooltip data-id="element-4929" />} cursor={{
                            fill: 'transparent'
                          }} data-id="element-4928" />
                                  <Bar dataKey="amount" radius={[4, 4, 0, 0]} maxBarSize={50} data-id="element-4930">
                                    {type.chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={index === 0 ? type.colorPrimary : type.colorSecondary} data-id="element-4931" />)}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Date List */}
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4932">
                              {/* Expanded Date Row */}
                              <div className="border-b border-gray-200" data-id="element-4933">
                                <button onClick={() => toggleDate(type.key, type.chartData[0]?.date + ' 2025')} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors" data-id="element-4934">
                                  <div className="flex items-center gap-3" data-id="element-4935">
                                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4936" />
                                    <span className="text-sm font-medium text-gray-900" data-id="element-4937">
                                      {type.chartData[0]?.date} 2025
                                    </span>
                                    <span className={`px-2 py-0.5 text-xs font-medium ${type.colorBadgeText} ${type.colorBadgeBg} border ${type.colorBadgeBorder} rounded-full`} data-id="element-4938">
                                      {type.badgeText}
                                    </span>
                                    <span className="text-xs text-gray-400" data-id="element-4939">
                                      {type.transactions.length} txns
                                    </span>
                                  </div>
                                  <span className={`text-sm font-semibold ${type.colorBadgeText}`} data-id="element-4940">
                                    THB{' '}
                                    {formatCurrency(type.chartData[0]?.amount || 0)}
                                  </span>
                                </button>

                                {expandedDates[type.key] === type.chartData[0]?.date + ' 2025' && <div className="px-4 pb-4" data-id="element-4941">
                                    <table className="w-full" data-id="element-4942">
                                      <thead data-id="element-4943">
                                        <tr data-id="element-4944">
                                          <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-4945">
                                            Transaction
                                          </th>
                                          <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-4946">
                                            Customer
                                          </th>
                                          <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-4947">
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100" data-id="element-4948">
                                        {type.transactions.map(txn => <tr key={txn.id} data-id="element-4949">
                                            <td className="py-3 text-sm text-gray-600 font-mono" data-id="element-4950">
                                              {txn.id}
                                            </td>
                                            <td className="py-3 text-sm text-gray-900" data-id="element-4951">
                                              {txn.customer}
                                            </td>
                                            <td className="py-3 text-sm font-medium text-gray-900 text-right" data-id="element-4952">
                                              THB {formatCurrency(txn.amount)}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                    <button className={`mt-4 flex items-center gap-1 text-sm font-medium ${type.colorBadgeText} hover:opacity-80 transition-opacity`} data-id="element-4953">
                                      View all activity{' '}
                                      <ArrowRightIcon size={14} data-id="element-4954" />
                                    </button>
                                  </div>}
                              </div>

                              {/* Collapsed Date Rows */}
                              {type.collapsedDates.map(item => <button key={item.date} className="w-full flex items-center justify-between px-4 py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors" data-id="element-4955">
                                  <div className="flex items-center gap-3" data-id="element-4956">
                                    <ChevronRightIcon size={16} className="text-gray-300" data-id="element-4957" />
                                    <span className="text-sm font-medium text-gray-700" data-id="element-4958">
                                      {item.date}
                                    </span>
                                    <span className="text-xs text-gray-400" data-id="element-4959">
                                      {item.count} txns
                                    </span>
                                  </div>
                                  <span className="text-sm font-medium text-gray-900" data-id="element-4960">
                                    THB {formatCurrency(item.amount)}
                                  </span>
                                </button>)}
                            </div>
                          </>}
                      </div>}
                  </div>;
            })}

              {/* Total */}
              <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50" data-id="element-4961">
                <span className="text-sm font-bold text-gray-900" data-id="element-4962">
                  Total balance
                </span>
                <span className="text-sm font-bold text-gray-900" data-id="element-4963">
                  THB 82,967.25
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SET MINIMUM BALANCE MODAL ─── */}
      {showMinBalanceModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setShowMinBalanceModal(false);
    }} data-id="element-4964">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-4965">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-4966">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-4967">
                  Set minimum balance
                </h3>
                <button onClick={() => setShowMinBalanceModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-4968">
                  <XIcon size={18} data-id="element-4969" />
                </button>
              </div>
              <div className="p-6 space-y-4" data-id="element-4970">
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4971">
                  When your balance drops below this amount, we'll notify you
                  and can automatically top up from your linked bank account.
                </p>
                <div data-id="element-4972">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-4973">
                    Minimum balance (THB)
                  </label>
                  <div className="relative" data-id="element-4974">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" data-id="element-4975">
                      THB
                    </span>
                    <input type="number" className="block w-full pl-12 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400" placeholder="e.g., 5,000" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} autoFocus data-id="element-4976" />
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-id="element-4977">
                  <div className="flex items-start gap-2" data-id="element-4978">
                    <AlertTriangleIcon size={14} className="text-amber-500 flex-shrink-0 mt-0.5" data-id="element-4979" />
                    <p className="text-xs text-amber-800 leading-relaxed" data-id="element-4980">
                      Your current balance is{' '}
                      <span className="font-semibold" data-id="element-4981">
                        THB {formatCurrency(Math.abs(displayBalance))}
                      </span>{' '}
                      in deficit. Setting a minimum will help prevent this in
                      the future.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-4982">
                <button onClick={() => setShowMinBalanceModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-4983">
                  Cancel
                </button>
                <button onClick={() => {
            if (minBalanceAmount && Number(minBalanceAmount) > 0) {
              setMinBalanceSaved(true);
              setShowMinBalanceModal(false);
              setShowMinBanner(false);
            }
          }} disabled={!minBalanceAmount || Number(minBalanceAmount) <= 0} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${minBalanceAmount && Number(minBalanceAmount) > 0 ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-4984">
                  Save minimum
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}